import { Lexer } from 'marked'

export interface ChangelogSection {
  id: string
  title: string
  version: string | null
  date: string | null
  markdown: string
}

export const RELEASE_VERSION_CORE =
  '(?:0|[1-9]\\d*)\\.(?:0|[1-9]\\d*)\\.(?:0|[1-9]\\d*)(?:-[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?(?:\\+[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?'
const VERSION_HEADING = new RegExp(
  `^\\[?(?:axilon-prophet/)?v?(${RELEASE_VERSION_CORE})\\]?(?:\\s+[—–-]\\s+(\\d{4}-\\d{2}-\\d{2}))?$`
)

export function normalizeReleaseVersion(version: string): string {
  return version.replace(/^axilon-prophet\//, '').replace(/^v/, '')
}

function compareIdentifiers(a: string, b: string): number {
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
    const left = BigInt(a)
    const right = BigInt(b)
    return left < right ? -1 : left > right ? 1 : 0
  }
  if (/^\d+$/.test(a)) return -1
  if (/^\d+$/.test(b)) return 1
  return a < b ? -1 : a > b ? 1 : 0
}

function compareVersions(a: string, b: string): number {
  const [aCore, aPre] = a.split('+')[0].split(/-(.*)/s)
  const [bCore, bPre] = b.split('+')[0].split(/-(.*)/s)
  const aParts = aCore.split('.')
  const bParts = bCore.split('.')
  for (let i = 0; i < 3; i++) {
    const order = compareIdentifiers(aParts[i], bParts[i])
    if (order) return order
  }
  if (aPre === undefined || bPre === undefined) {
    return aPre === bPre ? 0 : aPre === undefined ? 1 : -1
  }
  const left = aPre.split('.')
  const right = bPre.split('.')
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    if (left[i] === undefined) return -1
    if (right[i] === undefined) return 1
    const order = compareIdentifiers(left[i], right[i])
    if (order) return order
  }
  return 0
}

/** Tokenize only to find root headings; render the original Markdown slices. */
export function parseCustomerChangelog(source: string): ChangelogSection[] {
  const markdown = source.replace(/\r\n?/g, '\n')
  const tokens = Lexer.lex(markdown)
  const headings: { start: number; end: number; title: string }[] = []
  let offset = 0
  for (const token of tokens) {
    const start = markdown.indexOf(token.raw, offset)
    if (start < 0) return []
    offset = start + token.raw.length
    if (token.type === 'heading' && token.depth === 2) {
      headings.push({ start, end: offset, title: token.text.trim() })
    }
  }
  // Reference definitions are document-scoped, even when declared in another release.
  const definitions = Object.entries(tokens.links)
    .map(([label, link]) => {
      const href = link.href.replace(/</g, '%3C').replace(/>/g, '%3E')
      const title = link.title ? ` "${link.title.replace(/["\\]/g, '\\$&')}"` : ''
      return `[${label}]: <${href}>${title}`
    })
    .join('\n')
  const sections = headings.flatMap((heading, index) => {
    const body = markdown.slice(heading.end, headings[index + 1]?.start ?? markdown.length).trim()
    if (/^\[?unreleased\]?$/i.test(heading.title) && body === '') return []
    const match = VERSION_HEADING.exec(heading.title)
    const date = match?.[2]
    const validDate =
      date && !Number.isNaN(Date.parse(date)) && new Date(date).toISOString().slice(0, 10) === date
    return [
      {
        id: `release-notes-${index}`,
        title: match ? `v${match[1]}` : heading.title,
        version: match?.[1] ?? null,
        date: validDate ? date : null,
        markdown: body && definitions ? `${body}\n\n${definitions}` : body
      }
    ]
  })
  // Sort versions semantically; preserve source order for Unreleased and other notes.
  const versions = sections
    .filter((section) => section.version !== null)
    .sort((a, b) => compareVersions(b.version!, a.version!))
  return [...versions, ...sections.filter((section) => section.version === null)]
}
